import React, { useEffect, useState } from "react";
import {
  StyledAiSettingDescription,
  StyledAiSettingTitle,
  StyledHint,
} from "./AiTools.styles";
import { Box } from "@mui/material";
import AutoCompleteDropdown from "@metacrm/metacrm-material-ui/dist/AutoCompleteDropdown";
import Switch from "@metacrm/metacrm-material-ui/dist/Switch";
import { useConfirm } from "features/home/ConfirmDialogProvider";
import axios from "axios";
import { apiUrl } from "features/configure";
import { useOutletContext } from "react-router";
import useDispatchSnackbar from "hooks/useDispatchSnackbar";
import useNavigateEntity from "hooks/useNavigateEntity";
import {
  useFetchEntityConfig,
  useFetchWidgetConfig,
} from "features/metadesk/redux/hooks";
import { useIntl } from "react-intl";
import TestGround from "../aiTestGround/TestGround";
import { Loading } from "features/common";
import useGetPagePermission from "hooks/useGetPagePermission";
import {
  selectAiSupportConfig,
  selectCommunityTicketBased,
} from "features/metadesk/redux/entityConfig/entityConfig.selector";
import { useSelector } from "react-redux";

export default function AiTools() {
  const [formData, setFormData] = useState({
    tone: "Formal",
    responseLength: "Short",
    responseLanguage: "Auto Detect",
    connected: false,
  });

  const { formatMessage } = useIntl();

  const { tone, responseLength, responseLanguage, connected } = formData;

  const toneOptions = [
    formatMessage({ id: "global.aiSupport.responseConfig.tone.formal" }),
    formatMessage({ id: "global.aiSupport.responseConfig.tone.friendly" }),
    formatMessage({ id: "global.aiSupport.responseConfig.tone.casual" }),
  ];
  const lengthOptions = [
    formatMessage({ id: "global.aiSupport.responseConfig.length.short" }),
    formatMessage({ id: "global.aiSupport.responseConfig.length.medium" }),
    formatMessage({ id: "global.aiSupport.responseConfig.length.long" }),
  ];
  const languageOptions = [
    formatMessage({ id: "global.aiSupport.responseConfig.language.english" }),
    formatMessage({
      id: "global.aiSupport.responseConfig.language.autoDetect",
    }),
  ];

  const [entityName] = useOutletContext();
  const dispatchSnackbar = useDispatchSnackbar();

  const { fetchWidgetConfig, widgetConfig } = useFetchWidgetConfig();
  const { updateAiSupportConfig } = useFetchEntityConfig();

  const aiSupportConfig = useSelector(selectAiSupportConfig);
  const communityTicketBased = useSelector(selectCommunityTicketBased);

  useEffect(() => {
    if (aiSupportConfig) {
      setFormData((prev) => ({
        ...prev,
        ...aiSupportConfig,
      }));
    }
  }, [aiSupportConfig]);

  useEffect(() => {
    fetchWidgetConfig({ entityName });
  }, []);

  async function saveOption(option) {
    const result = { ...formData, ...option };
    try {
      setFormData(result);

      // 使用新的 updateAiSupportConfig 函数
      await updateAiSupportConfig({
        entityName,
        tone: result.tone,
        responseLength: result.responseLength,
        responseLanguage: result.responseLanguage,
        connected: result.connected,
      });

      dispatchSnackbar({
        variant: "success",
        message: formatMessage({ id: "aiTools.toast.settingsSaved" }),
      });
    } catch (err) {
      dispatchSnackbar({ err });
    }
  }

  const confirm = useConfirm();
  const { navigateEntity } = useNavigateEntity();

  const [isLoading, setIsLoading] = useState(false);
  const pagePermission = useGetPagePermission();
  const { readonly } = pagePermission("aiManagement");

  useEffect(() => {
    // fetch api
    async function fetchAiSetting() {
      try {
        setIsLoading(true);
        const { data } = await axios.get(
          `${apiUrl}/api/manage/aiSupport/${entityName}`
        );
        const {
          responseLanguage,
          responseLength,
          tone,
          connected = false,
          widgetVersionCheck,
          knowledgeBaseResourceCount,
        } = data;
        setFormData((prev) => ({
          ...prev,
          tone,
          connected,
          responseLength,
          responseLanguage,
          widgetVersionCheck,
          knowledgeBaseResourceCount,
        }));
      } catch (err) {
        dispatchSnackbar({ err });
      } finally {
        setIsLoading(false);
      }
    }
    fetchAiSetting();
  }, []);

  const { widgetVersionCheck, knowledgeBaseResourceCount } = formData;
  const renderHint = () => {
    if (isLoading) return;
    if (!widgetVersionCheck && widgetConfig?.length > 0) {
      return (
        <StyledHint>
          <i
            className="meta-crm-icon-ic_info_fill font-size-18"
            style={{ color: "#7B61FF", marginRight: "8px" }}
          />
          <Box color="#222">
            {formatMessage({ id: "global.aiSupport.warning.outdatedWidget" })}
            <span
              style={{
                color: "#7B61FF",
                textDecoration: "underline",
                cursor: "pointer",
                marginLeft: "4px",
              }}
              onClick={() => {
                navigateEntity("/settings/general/app/update-list");
              }}
            >
              {formatMessage({ id: "global.aiSupport.button.goToUpdate" })}
            </span>{" "}
          </Box>
        </StyledHint>
      );
    }

    if (knowledgeBaseResourceCount === 0) {
      return (
        <StyledHint>
          <i
            className="meta-crm-icon-ic_info_fill font-size-18"
            style={{ color: "#7B61FF", marginRight: "8px" }}
          />
          <Box color="#222">
            {formatMessage(
              { id: "global.aiSupport.warning.noKnowledgeBase" },
              {
                knowledge: (
                  <span
                    style={{
                      color: "#7B61FF",
                      textDecoration: "underline",
                      cursor: "pointer",
                    }}
                    onClick={() => {
                      navigateEntity("/settings/ai/knowledgeBase");
                    }}
                  >
                    {formatMessage({ id: "global.function.knowledgeBase" })}
                  </span>
                ),
              }
            )}
          </Box>
        </StyledHint>
      );
    }

    return null;
  };

  return (
    <div>
      <Loading open={isLoading} />
      <div className="title">
        {formatMessage({ id: "global.aiTools.title" })}
      </div>
      <div className="description">
        {formatMessage({ id: "global.aiSupport.description" })}
      </div>
      <Box sx={{ display: "flex", marginTop: "24px", gap: "0 12px" }}>
        <Box sx={{ flex: 1 }}>
          {renderHint()}
          <Box bgcolor="#fff" borderRadius="4px">
            <Box
              sx={{
                p: "20px 24px",
                borderBottom: "1px solid #E6E6E6",
                display: "flex",
              }}
            >
              <Box>
                <StyledAiSettingTitle mb="4px">
                  {formatMessage({ id: "global.aiSupport.toggle.label" })}
                </StyledAiSettingTitle>
                <StyledAiSettingDescription>
                  {formatMessage({ id: "global.aiSupport.description" })}
                </StyledAiSettingDescription>
              </Box>
              <Switch
                iosSwitch
                checked={connected}
                disabled={readonly}
                onChange={(value, e) => {
                  if (knowledgeBaseResourceCount === 0) {
                    return confirm({
                      width: "360px",
                      title: formatMessage({
                        id: "popup.knowledgeBaseRequired.title",
                      }),
                      content: formatMessage({
                        id: "popup.knowledgeBaseRequired.description",
                      }),
                      confirmText: formatMessage({ id: "global.btn.setup" }),
                      cancelText: formatMessage({ id: "global.btn.cancel" }),
                      color: "secondary",
                      cancelColor: "secondary",
                    })
                      .then(() => {
                        navigateEntity("/settings/ai/knowledgeBase");
                      })
                      .catch(() => {});
                  }
                  if (value) {
                    confirm({
                      width: "337px",
                      title: formatMessage({
                        id: "popup.startAiSupport.title",
                      }),
                      content: formatMessage({
                        id: "popup.startAiSupport.description",
                      }),
                      confirmText: formatMessage({ id: "global.btn.enable" }),
                      cancelText: formatMessage({ id: "global.btn.cancel" }),
                      color: "secondary",
                      cancelColor: "secondary",
                    })
                      .then(() => {
                        saveOption({ connected: true });
                      })
                      .catch(() => {});
                  } else {
                    confirm({
                      width: "353px",
                      title: formatMessage({
                        id: "popup.disableAiSupport.title",
                      }),
                      content: formatMessage({
                        id: communityTicketBased
                          ? "popup.disableAiSupport.description"
                          : "popup.disableChatSupport.description",
                      }),
                      confirmText: formatMessage({ id: "global.btn.disable" }),
                      cancelText: formatMessage({ id: "global.btn.cancel" }),
                      color: "secondary",
                      cancelColor: "secondary",
                    })
                      .then(() => {
                        saveOption({ connected: false });
                      })
                      .catch(() => {});
                  }
                }}
              />
            </Box>
            <Box sx={{ p: "20px 24px" }}>
              <StyledAiSettingTitle mb="4px">
                {formatMessage({ id: "global.aiSupport.responseConfig.title" })}
              </StyledAiSettingTitle>
              <StyledAiSettingDescription>
                {formatMessage({
                  id: "global.aiSupport.responseConfig.description",
                })}
              </StyledAiSettingDescription>
              <Box mt="20px">
                <Box mb="4px">
                  {formatMessage({
                    id: "global.aiSupport.responseConfig.tone.label",
                  })}
                </Box>
                <AutoCompleteDropdown
                  readonly={readonly}
                  fullWidth={true}
                  options={toneOptions?.map((item) => ({
                    label: item,
                    value: item,
                  }))}
                  value={{ label: tone, value: tone }}
                  //   control={control}
                  onChange={(e, v) => {
                    saveOption({ tone: v.value });
                  }}
                />
              </Box>
              <Box mt="20px">
                <Box mb="4px">
                  {formatMessage({
                    id: "global.aiSupport.responseConfig.length.label",
                  })}
                </Box>
                <AutoCompleteDropdown
                  readonly={readonly}
                  fullWidth={true}
                  options={lengthOptions?.map((item) => ({
                    label: item,
                    value: item,
                  }))}
                  value={{ label: responseLength, value: responseLength }}
                  //   control={control}
                  onChange={(e, v) => {
                    saveOption({ responseLength: v.value });
                  }}
                />
              </Box>
              <Box mt="20px">
                <Box mb="4px">
                  {formatMessage({
                    id: "global.aiSupport.responseConfig.language.label",
                  })}
                </Box>
                <AutoCompleteDropdown
                  readonly={readonly}
                  fullWidth={true}
                  placeholder={""}
                  options={languageOptions?.map((item) => ({
                    label: item,
                    value: item,
                  }))}
                  value={{ label: responseLanguage, value: responseLanguage }}
                  //   control={control}
                  onChange={(e, v) => {
                    saveOption({ responseLanguage: v.value });
                  }}
                />
              </Box>

              <Box mt="20px" color="#7B61FF" display="flex" fontSize={12}>
                <i
                  className="meta-crm-icon-ic_info font-size-16"
                  style={{ marginRight: "4px" }}
                />
                <Box>
                  {formatMessage(
                    { id: "global.aiSupport.responseConfig.note" },
                    {
                      Integration: (
                        <Box
                          component="span"
                          sx={{
                            cursor: "pointer",
                            textDecoration: "underline",
                          }}
                          onClick={() =>
                            navigateEntity("/settings/general/integration")
                          }
                        >
                          Integration
                        </Box>
                      ),
                    }
                  )}
                </Box>
              </Box>
              {/* <Box mt="20px">
                <Box mb="4px">Fallback Message</Box>
                <InputField
                  multiline
                  placeholder={
                    "If left blank, the default fallback message will be: 'This question is best handled by a human agent. Do you want to connect to a human agent?'"
                  }
                  value={fallbackMessage}
                  //   control={control}
                  onChange={(e) => {
                    setFormData({
                      ...formData,
                      fallbackMessage: e.target.value,
                    });
                  }}
                  maxLength={2000}
                  inputProps={{
                    style: {
                      height: "120px",
                    },
                  }}
                />
              </Box> */}
            </Box>
          </Box>
        </Box>
        <TestGround />
      </Box>
    </div>
  );
}
